function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Management-fee rows come back from DynamoDB in arbitrary order, so an ISP
// can render breakpoints like "First $100,000 / Next $500,000 / Next
// $150,000 ...". Re-order them into the canonical asset-level sequence:
// the "First $X" band, then every "Next $Y" band ascending by Y, then the
// terminal "Over $Z" band. Rows whose label doesn't match that pattern keep
// their original relative position at the end.
function tierSortKey(label) {
  const text = String(label || "").trim();
  const amount = (() => {
    const m = text.match(/\$\s*([\d,]+)/);
    return m ? Number(m[1].replace(/,/g, "")) : Number.POSITIVE_INFINITY;
  })();
  if (/^first\b/i.test(text)) return [0, amount];
  if (/^next\b/i.test(text)) return [1, amount];
  if (/^over\b/i.test(text)) return [2, amount];
  return [3, Number.POSITIVE_INFINITY];
}

export function sortFeeTiers(tieredRates) {
  if (!Array.isArray(tieredRates)) return tieredRates;
  return tieredRates
    .map((tier, index) => ({
      tier,
      index,
      key: tierSortKey(typeof tier === "string" ? tier : tier?.label),
    }))
    .sort((a, b) => a.key[0] - b.key[0] || a.key[1] - b.key[1] || a.index - b.index)
    .map((entry) => entry.tier);
}

// Apply sortFeeTiers in place to a parsed management-fees object's
// tieredRates array (if present). Returns the same object for convenience.
export function sortManagementFeeTiers(fees) {
  if (fees && Array.isArray(fees.tieredRates)) {
    fees.tieredRates = sortFeeTiers(fees.tieredRates);
  }
  return fees;
}

function normalizeTieredRates(feeTable) {
  if (!Array.isArray(feeTable?.tieredRates)) {
    return [];
  }

  return feeTable.tieredRates
    .map((tier) => {
      const label = String(tier?.label || "").trim();
      const rate = toNumber(tier?.rate);
      if (!label || rate === null) {
        return null;
      }

      return { label, rate };
    })
    .filter(Boolean);
}

export function validateManagementFeeTable(feeTable) {
  const tieredRates = normalizeTieredRates(feeTable);
  const minimumFee =
    feeTable?.minimumFee === undefined || feeTable?.minimumFee === null || feeTable?.minimumFee === ""
      ? null
      : toNumber(feeTable.minimumFee);

  return {
    isComplete: tieredRates.length > 0,
    tieredRates,
    minimumFee,
  };
}

export function getFusionFeeValidation(allocations = [], feeTables = {}, getFeeKey) {
  const issues = [];

  allocations.forEach((allocation) => {
    const strategy = String(allocation?.strategy || "").trim();
    const percentage = toNumber(allocation?.percentage);

    if (!strategy || percentage === null || percentage <= 0) {
      return;
    }

    const feeKey = typeof getFeeKey === "function" ? getFeeKey(strategy) : strategy;
    const validation = validateManagementFeeTable(feeTables?.[feeKey]);

    if (!validation.isComplete) {
      issues.push({
        strategy,
        feeKey,
        message: `${strategy} does not have a complete management fee schedule for the selected tier.`,
      });
    }
  });

  return {
    isValid: issues.length === 0,
    issues,
  };
}

export function computeBlendedFees(allocations = [], feeTables = {}, getFeeKey) {
  const validAllocations = allocations
    .map((allocation) => ({
      strategy: String(allocation?.strategy || "").trim(),
      percentage: toNumber(allocation?.percentage),
    }))
    .filter((allocation) => allocation.strategy && allocation.percentage !== null && allocation.percentage > 0);

  if (validAllocations.length === 0) {
    return null;
  }

  const validation = getFusionFeeValidation(validAllocations, feeTables, getFeeKey);
  if (!validation.isValid) {
    return null;
  }

  const totalWeight = validAllocations.reduce((sum, allocation) => sum + allocation.percentage, 0);
  if (!totalWeight) {
    return null;
  }

  const firstKey = typeof getFeeKey === "function"
    ? getFeeKey(validAllocations[0].strategy)
    : validAllocations[0].strategy;
  const firstValidation = validateManagementFeeTable(feeTables?.[firstKey]);
  if (!firstValidation.isComplete) {
    return null;
  }

  const blendedRates = firstValidation.tieredRates.map((baseTier, index) => {
    let weightedRate = 0;

    validAllocations.forEach((allocation) => {
      const feeKey = typeof getFeeKey === "function" ? getFeeKey(allocation.strategy) : allocation.strategy;
      const tierRates = validateManagementFeeTable(feeTables?.[feeKey]).tieredRates;
      const matchingTier = tierRates[index];
      weightedRate += (matchingTier.rate * allocation.percentage) / totalWeight;
    });

    return {
      label: baseTier.label,
      rate: Number(weightedRate.toFixed(4)),
    };
  });

  let blendedMinimumFee = 0;
  let hasMinimumFee = false;

  validAllocations.forEach((allocation) => {
    const feeKey = typeof getFeeKey === "function" ? getFeeKey(allocation.strategy) : allocation.strategy;
    const { minimumFee } = validateManagementFeeTable(feeTables?.[feeKey]);
    if (minimumFee !== null) {
      hasMinimumFee = true;
      blendedMinimumFee += (minimumFee * allocation.percentage) / totalWeight;
    }
  });

  return {
    tieredRates: blendedRates,
    minimumFee: hasMinimumFee ? Number(blendedMinimumFee.toFixed(2)) : null,
  };
}
